import { ACCOUNTING_METHOD_TYPES } from "constants/bookkeeping";
import { FINANCIAL_CLOSING_CARD_TYPE } from "constants/chatType";
import { RND_ONBOARDING_STEP } from "constants/financialClosing";
import {
  CHANGE_REQUEST_STATUS,
  CHECKLIST_ITEM_SECTION,
  CHECKLIST_ITEM_TYPE,
  CLOSING_STATUS,
  CLOSING_SUMMARY_KEY,
  VARIANCE_REPORT_STATUS,
} from "constants/financialClosing";
import qs from "qs";
import { Comment } from "types/Models/books";
import { FileObject, FileObjectDocument } from "types/Models/fileObject";
import { emptyApi } from "./emptyApi";

export type BooksQuote = {
  cash_expense: string;
  transactions_count: number;
  stripe_transaction_count: number;
  credit_card_transaction_count: number;
  bank_transaction_count: number;
  month: string;
  year: string;
  transfers_within_entity: number;
  transfers_across_entities: number;
  billable: number;
  subscriptions: Record<string, number>;
};

type ClosingActor = { name: string | null; uuid: string | null };

export type ClosingChangeRequest = {
  uuid: string;
  reason: string;
  created_by: ClosingActor;
  created_at: string;
  status: (typeof CHANGE_REQUEST_STATUS)[keyof typeof CHANGE_REQUEST_STATUS];
};

export type FinancialClosingDetail = {
  uuid: string;
  accounting_method: ACCOUNTING_METHOD_TYPES;
  period: string;
  status: (typeof CLOSING_STATUS)[keyof typeof CLOSING_STATUS];
  rejected_at: string | null;
  rejection_reason: string | null;
  checklist_status: {
    completed: number;
    total: number;
  };
  income_statement: FileObject | null;
  balance_sheet: FileObject | null;
  cash_flow_statement: FileObject | null;
  balance_sheet_url: null;
  income_statement_url: null;
  cash_flow_statement_url: null;
  comments: {
    notes: [];
  };
  start_date: string;
  end_date: string;
  creator: {
    name: string;
    email: string;
    is_service_team_user: boolean;
    created_at: string;
  };
  reviewer: {
    name: string;
    uuid: string;
    reviewed_at: string;
  };
  first_completed_at: string | null;
  amended_at: string | null;
  amended_by: { name: string; uuid: string } | null;
  version: number;
  original_closing_id: string | null;
  change_requests: ClosingChangeRequest[];
  earlier_versions?: FinancialClosingDetail[];
  earlier_versions_count?: number;
  original_sent_at?: string | null;
  preparer: string | null;
  other_financials: FileObject[];
  accounting_lock_date: string | null;
  document_file: FileObjectDocument | null;
  review_message_id: string | null;
  show_summary: boolean;
  cash_flow_statement_document_type: string;
  balance_sheet_document_type: string;
  income_statement_document_type: string;
  balance_sheet_generate_link: string;
  income_statement_generate_link: string;
  cash_flow_statement_generate_link: string;
  variance_report_status: (typeof VARIANCE_REPORT_STATUS)[keyof typeof VARIANCE_REPORT_STATUS];
};

export type ChecklistItem = {
  name: string;
  description: string;
  uuid: string;
  type: (typeof CHECKLIST_ITEM_TYPE)[keyof typeof CHECKLIST_ITEM_TYPE];
  section: (typeof CHECKLIST_ITEM_SECTION)[keyof typeof CHECKLIST_ITEM_SECTION];
  is_visible: boolean;
  checklist_item_status: string;
  resolved_by_admin: boolean;
  resolved_by_system: boolean;
  entity_bank_account: {
    uuid: string;
    name: string;
    mask: string;
  };
  file_data?: FileObject | null;
  file_url?: string;
  document_type?: string;
  summary_sent_to_chat?: boolean;
};

type SummaryKeys =
  (typeof CLOSING_SUMMARY_KEY)[keyof typeof CLOSING_SUMMARY_KEY];

export type Summary = {
  [key in SummaryKeys]: {
    value: number;
    title: string;
  };
};

export type FinancialClosing = {
  closing_card_details: FinancialClosingDetail;
  checklist_items: ChecklistItem[];
  other_financials: FileObject[];
};

export type UpdateOtherAttachments = {
  entityId: string;
  financialClosingId: string;
  otherFinancialId: string;
  payload: {
    document_type?: string;
    document_name?: string;
    document_file?: File;
    document_id?: string;
  };
};

export type FinancialClosingDocumentSummaryData =
  | {
      success: true;
      summary: string;
      summary_sent_to_chat: boolean;
      questions_and_answers: any[];
    }
  | {
      success: false;
      summary?: string;
      summary_sent_to_chat?: boolean;
      questions_and_answers?: any[];
      error?: string;
    };

export type RnDVendor = {
  vendor_id: string;
  vendor_name: string;
  total_amount: string;
  is_us: boolean;
  us_rd_percentage: string;
  us_non_rd_percentage: string;
  is_non_us: boolean;
  non_us_rd_percentage: string;
  non_us_non_rd_percentage: string;
  remarks: string;
};

export type RnDBifurcationFieldName =
  | "is_us"
  | "us_rd_percentage"
  | "is_non_us"
  | "non_us_rd_percentage"
  | "remarks"
  | "is_excluded";

export type RnDBifurcationSectionFieldName = "share_with_client";

export type RnDBifurcationFieldValue = boolean | number | string;

export type RnDBifurcationSection = {
  vendors: RnDVendor[];
  previous_month_reference: RnDVendor[];
  previous_month_date: string | null;
};

export type ProjectedTaxImpact = {
  total_expense: string;
  non_us_rd_expense: string;
  allowed_deduction: string;
  disallowed_amount: string;
  net_income: string;
  adjusted_net_income: string;
  estimated_tax_liability: string;
  amortization_period: number;
};

export type RnDOnboardingStep =
  (typeof RND_ONBOARDING_STEP)[keyof typeof RND_ONBOARDING_STEP];

export type RnDBifurcationResponse = {
  intercompany_compensation: RnDBifurcationSection | null;
  consultant_contractors: RnDBifurcationSection | null;
  projected_tax_impact: ProjectedTaxImpact | null;
  share_with_client: boolean;
  rnd_onboarding_step: number;
  rnd_review_completed: boolean;
};

const AMENDMENT_INVALIDATION_TAGS = [
  "FINANCIAL_CLOSING",
  "FINANCIAL_CLOSING_SUMMARY",
  "ALL_ACTION_ITEMS",
  "ChatTickets",
] as const;

export const financialClosingApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllFinancialClosing: build.query<
      FinancialClosingDetail[],
      {
        entityId: string;
        startDate?: string;
        endDate?: string;
        sortOrder?: "ASC" | "DSC";
        searchTerm?: string;
        closingStatus?: (typeof CLOSING_STATUS)[keyof typeof CLOSING_STATUS];
      }
    >({
      query: ({
        entityId,
        startDate,
        endDate,
        sortOrder,
        searchTerm,
        closingStatus,
      }) => {
        const query = qs.stringify(
          {
            start_date: startDate || null,
            end_date: endDate || null,
            sort_order: sortOrder,
            search_term: searchTerm || null,
            closing_status: closingStatus || null,
          },
          {
            skipNulls: true,
          }
        );
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/`,
          params: qs.parse(query),
        };
      },
      providesTags: (result) => ["FINANCIAL_CLOSING"],
    }),

    getFinancialClosing: build.query<
      FinancialClosing,
      {
        entityId: string;
        financialClosingId: string;
      }
    >({
      query: ({ entityId, financialClosingId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/`,
        };
      },
      providesTags: (result) => [
        "FINANCIAL_CLOSING",
        "SUMMARY_FINANCIAL_CLOSING",
      ],
    }),

    updateVarianceReportStatus: build.mutation<
      void,
      { entityId: string; financialClosingId: string }
    >({
      query: ({ entityId, financialClosingId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/variance_report/`,
        method: "PUT",
      }),
      invalidatesTags: ["FINANCIAL_CLOSING"],
    }),

    addFinancialClosing: build.mutation<
      FinancialClosingDetail,
      {
        entityId: string;
        payload: {
          accounting_method: "CASH" | "ACCRUAL";
          start_date: string;
          end_date: string;
          task_id?: string;
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/`,
          body: payload,
          method: "POST",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    updateFinancialClosing: build.mutation<
      FinancialClosingDetail,
      {
        entityId: string;
        financialClosingId: string;
        payload: {
          start_date?: string;
          end_date?: string;
          accounting_method?: "CASH" | "ACCRUAL";
          show_summary?: boolean;
        };
      }
    >({
      query: ({ entityId, financialClosingId, payload }) => {
        const formData = new FormData();

        if (payload.start_date) {
          formData.append("start_date", payload.start_date);
        }
        if (payload.end_date) {
          formData.append("end_date", payload.end_date);
        }
        if (payload.accounting_method) {
          formData.append("accounting_method", payload.accounting_method);
        }
        if (payload.show_summary !== undefined) {
          formData.append("show_summary", String(payload.show_summary));
        }

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/`,
          body: formData,
          method: "PATCH",
        };
      },
      invalidatesTags: (result) =>
        result ? ["FINANCIAL_CLOSING", "RnDBifurcation"] : [],
    }),

    addOtherFinancials: build.mutation<
      FinancialClosingDetail,
      {
        entityId: string;
        financialClosingId: string;
        payload?: {
          other_financials?: FileObjectDocument[];
          file_document_ids?: string[];
          document_type?: string;
          document_name?: string;
          document_file?: FileObjectDocument;
          document_url?: string;
        };
      }
    >({
      query: ({ entityId, financialClosingId, payload }) => {
        const formData = new FormData();

        if (payload) {
          if (payload.document_type) {
            formData.append("document_type", payload.document_type);
          }
          if (payload.document_name) {
            formData.append("document_name", payload.document_name);
          }
          if (payload.document_file) {
            formData.append(
              "document_file",
              payload.document_file as unknown as File
            );
          }

          if (payload.document_url) {
            formData.append("document_url", payload.document_url);
          }
        }

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/other_financials/`,
          body: formData,
          method: "POST",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    deleteOtherFinancials: build.mutation<
      FinancialClosingDetail,
      {
        entityId: string;
        financialClosingId: string;
        financialId: string;
      }
    >({
      query: ({ entityId, financialClosingId, financialId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/other_financials/`,
          body: {
            delete_financials_ids: [financialId],
          },
          method: "DELETE",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    updateFinancialClosingStatus: build.mutation<
      FinancialClosingDetail,
      {
        entityId: string;
        financialClosingId: string;
        payload: {
          status?: FinancialClosingDetail["status"];
          accounting_lock_date?: string | null;
          send_completed_financials_email?: boolean;
          rejection_reason?: string;
        };
      }
    >({
      query: ({ entityId, financialClosingId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/`,
          body: payload,
          method: "PUT",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    updateFinancialClosingCheckList: build.mutation<
      ChecklistItem,
      {
        entityId: string;
        financialClosingId: string;
        checklistItemId: string;
        payload: {
          admin_resolved?: boolean;
          file_data?: File;
          file_url?: string;
          document_id?: string;
          document_type?: string;
          delete_document?: boolean;
        };
      }
    >({
      query: ({ entityId, financialClosingId, checklistItemId, payload }) => {
        const form = new FormData();

        form.append(
          "admin_resolved",
          payload.admin_resolved === true ? "true" : "false"
        );

        if (payload.file_url) {
          form.append("file_url", payload.file_url || "");
        }

        if (payload.file_data) {
          form.append("file_data", payload.file_data || "");
        }
        if (payload.document_id) {
          form.append("document_id", payload.document_id || "");
        }

        if (payload.document_type) {
          form.append("document_type", payload.document_type || "");
        }
        if (payload.delete_document) {
          form.append("file_data", String("undefined"));
        }
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/checklist_items/${checklistItemId}/`,
          body: form,
          method: "PATCH",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    deleteFinancialClosing: build.mutation<
      void,
      {
        entityId: string;
        financialClosingId: string;
      }
    >({
      query: ({ entityId, financialClosingId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    amendFinancialClosing: build.mutation<
      FinancialClosingDetail,
      { entityId: string; financialClosingId: string; reason?: string }
    >({
      query: ({ entityId, financialClosingId, reason }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/amend/`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (result) =>
        result ? [...AMENDMENT_INVALIDATION_TAGS] : [],
    }),

    resolveFinancialClosingRequest: build.mutation<
      FinancialClosingDetail,
      { entityId: string; financialClosingId: string }
    >({
      query: ({ entityId, financialClosingId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/resolve_request/`,
        method: "POST",
      }),
      invalidatesTags: (result) =>
        result ? [...AMENDMENT_INVALIDATION_TAGS] : [],
    }),

    deleteAllFinancialClosingVersions: build.mutation<
      void,
      { entityId: string; financialClosingId: string }
    >({
      query: ({ entityId, financialClosingId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/versions/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, error) =>
        error ? [] : [...AMENDMENT_INVALIDATION_TAGS],
    }),

    getFinancialClosingDocumentSummary: build.query<
      FinancialClosingDocumentSummaryData,
      {
        entityId: string;
        financialClosingId: string;
        documentId: string;
      }
    >({
      query: ({ entityId, financialClosingId, documentId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/document/${documentId}/summary/`,
        };
      },
    }),

    addSummaryToDocumentChannel: build.mutation<
      any,
      {
        entityId: string;
        channelUrl: string;
        text: string;
        parentMessageId: string;
        customType: string;
        metadata: {
          summary_document_id: string;
        };
      }
    >({
      query: ({
        entityId,
        channelUrl,
        text,
        parentMessageId,
        customType,
        metadata,
      }) => {
        return {
          url: `/api/inkle/messaging/entity/${entityId}/channel/${channelUrl}/send_channel_message/`,
          method: "POST",
          body: {
            text,
            custom_type: customType,
            parent_message_id: parentMessageId,
            metadata: metadata,
          },
        };
      },
      invalidatesTags: (result) =>
        result ? ["SUMMARY_FINANCIAL_CLOSING"] : [],
    }),

    getFinancialClosingSummary: build.query<
      Summary,
      {
        entityId: string;
        financialClosingId: string;
      }
    >({
      query: ({ entityId, financialClosingId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/summary/`,
        };
      },
      providesTags: (result) => [
        "FINANCIAL_CLOSING_SUMMARY",
        "FINANCIAL_CLOSING",
      ],
    }),

    updateFinancialClosingSummary: build.mutation<
      Summary,
      {
        entityId: string;
        financialClosingId: string;
        summary: Record<string, number>;
      }
    >({
      query: ({ entityId, financialClosingId, summary }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/summary/`,
          method: "PUT",
          body: { summary_data: summary },
        };
      },
      invalidatesTags: (result) =>
        result ? ["FINANCIAL_CLOSING_SUMMARY", "FINANCIAL_CLOSING"] : [],
    }),

    getFinancialClosingComments: build.query<
      { notes: Comment[] },
      {
        entityId: string;
        financialClosingId: string;
      }
    >({
      query: ({ entityId, financialClosingId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/comment/`,
        };
      },
      providesTags: (result) => ["FINANCIAL_CLOSING_COMMENTS", "NOTES"],
    }),

    addFinancialClosingComments: build.mutation<
      { notes: Comment[] },
      {
        entityId: string;
        financialClosingId: string;
        description: string;
      }
    >({
      query: ({ entityId, financialClosingId, description }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/comment/`,
          body: {
            description,
          },
          method: "POST",
        };
      },
      invalidatesTags: (result) =>
        result ? ["FINANCIAL_CLOSING_COMMENTS", "NOTES"] : [],
    }),

    sendClosingToChat: build.mutation<
      { message_id: string },
      {
        entityId: string;
        financialClosingId: string;
        card_type: keyof typeof FINANCIAL_CLOSING_CARD_TYPE;
        channel_url: string;
        message?: string;
        mentioned_stream_user_ids?: string[];
      }
    >({
      query: ({
        entityId,
        financialClosingId,
        card_type,
        channel_url,
        message,
        mentioned_stream_user_ids,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/push_to_chat/`,
          body: {
            card_type,
            channel_url,
            message,
            mentioned_stream_user_ids,
          },
          method: "POST",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    getBooksQuotes: build.query<
      BooksQuote[], // needs to be checked
      {
        groupId: string;
        entityId: string;
        year: string;
      }
    >({
      query: ({ groupId, entityId, year }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/quote/?year=${year}`,
        };
      },
      providesTags: ["BOOKS_QUOTES"],
    }),
    exportBooksQuotes: build.mutation<
      { excel_document_id: string },
      {
        groupId: string;
        entityId: string;
        year: string;
      }
    >({
      query: ({ groupId, entityId, year }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/quote/csv/?year=${year}`,
          method: "POST",
        };
      },
    }),
    updateOtherAttachments: build.mutation<
      UpdateOtherAttachments,
      {
        entityId: string;
        financialClosingId: string;
        otherFinancialId: string;
        payload: {
          document_type?: string;
          document_name?: string;
          document_file?: File;
          document_url?: string;
          delete_document?: boolean;
          document_id?: string;
        };
      }
    >({
      query: ({
        entityId,
        financialClosingId,
        otherFinancialId,
        payload,
      }: {
        entityId: string;
        financialClosingId: string;
        otherFinancialId: string;
        payload: {
          document_type?: string;
          document_name?: string;
          document_file?: File;
          document_url?: string;
          document_id?: string;
          isDeleted?: boolean;
          delete_document?: boolean;
        };
      }) => {
        const formData = new FormData();
        if (payload.document_type) {
          formData.append("document_type", payload.document_type);
        }
        if (payload.document_name) {
          formData.append("document_name", payload.document_name);
        }
        if (payload.document_url) {
          formData.append("document_url", payload.document_url);
        }
        if (payload.document_file) {
          formData.append("document_file", payload.document_file);
        }
        if (payload.delete_document) {
          formData.append(
            "delete_document",
            payload.delete_document.toString()
          );
        }
        if (payload.document_id) {
          formData.append("document_id", payload.document_id);
        }
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/other_financials/${otherFinancialId}/`,
          body: formData,
          method: "PATCH",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),
    deleteOtherAttachments: build.mutation<
      void,
      {
        entityId: string;
        financialClosingId: string;
        otherFinancialId: string;
      }
    >({
      query: ({ entityId, financialClosingId, otherFinancialId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/other_financials/${otherFinancialId}/`,
          method: "DELETE",
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),

    getRnDBifurcation: build.query<
      RnDBifurcationResponse,
      { entityId: string; financialClosingId: string }
    >({
      query: ({ entityId, financialClosingId }) =>
        `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/rnd_bifurcation/`,
      providesTags: ["RnDBifurcation"],
    }),

    updateRnDBifurcationField: build.mutation<
      { status: string },
      {
        entityId: string;
        financialClosingId: string;
        payload: {
          section_type: "INTERCOMPANY_COMPENSATION" | "CONSULTANT_CONTRACTOR";
          vendor_id: string;
          field: RnDBifurcationFieldName;
          value: RnDBifurcationFieldValue;
        };
      }
    >({
      query: ({ entityId, financialClosingId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/rnd_bifurcation/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["RnDBifurcation"],
    }),

    updateRnDBifurcationVisibility: build.mutation<
      { status: string },
      {
        entityId: string;
        financialClosingId: string;
        payload: {
          field: RnDBifurcationSectionFieldName;
          value: boolean;
        };
      }
    >({
      query: ({ entityId, financialClosingId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/rnd_bifurcation/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["RnDBifurcation"],
    }),

    updateRnDOnboardingStep: build.mutation<
      { status: string },
      {
        entityId: string;
        financialClosingId: string;
        payload: { step: RnDOnboardingStep; mark_completed?: boolean };
      }
    >({
      query: ({ entityId, financialClosingId, payload }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/rnd_bifurcation/onboarding_step/`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["RnDBifurcation"],
    }),

    updateChecklist: build.mutation<
      FinancialClosing,
      {
        entityId: string;
        financialClosingId: string;
        payload: {
          update_upcoming_closing_also?: boolean;
          checklist_visibility_list: {
            checklist_item_id: string;
            is_visible: boolean;
          }[];
          other_actions?: {
            uuid?: string;
            is_visible: boolean;
            name: string;
            description: string;
          }[];
        };
      }
    >({
      query: ({ entityId, financialClosingId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/financial_closing/${financialClosingId}/manage_checklist/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) => (result ? ["FINANCIAL_CLOSING"] : []),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllFinancialClosingQuery,
  useGetFinancialClosingQuery,
  useUpdateVarianceReportStatusMutation,
  useGetFinancialClosingDocumentSummaryQuery,
  useAddSummaryToDocumentChannelMutation,
  useLazyGetFinancialClosingQuery,
  useAddFinancialClosingMutation,
  useUpdateFinancialClosingMutation,
  useDeleteFinancialClosingMutation,
  useAmendFinancialClosingMutation,
  useResolveFinancialClosingRequestMutation,
  useDeleteAllFinancialClosingVersionsMutation,
  useUpdateFinancialClosingCheckListMutation,
  useGetFinancialClosingCommentsQuery,
  useGetFinancialClosingSummaryQuery,
  useAddFinancialClosingCommentsMutation,
  useUpdateFinancialClosingSummaryMutation,
  useUpdateFinancialClosingStatusMutation,
  useGetBooksQuotesQuery,
  useExportBooksQuotesMutation,
  useSendClosingToChatMutation,
  useAddOtherFinancialsMutation,
  useDeleteOtherFinancialsMutation,
  useUpdateOtherAttachmentsMutation,
  useDeleteOtherAttachmentsMutation,
  useUpdateChecklistMutation,
  useGetRnDBifurcationQuery,
  useUpdateRnDBifurcationFieldMutation,
  useUpdateRnDBifurcationVisibilityMutation,
  useUpdateRnDOnboardingStepMutation,
} = financialClosingApi;
